import React, { FC, useCallback, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import { getSpecificationsForGroup } from '@azgaz/azgaz-ui';

import { TBrandCar } from '@/selectors/getBrandCar';
import { getCreditCalcFormState, getImages } from '@/selectors';
import { format, WidgetMode } from '@/utils';
import { Disclaimers, TSortedDisclaimers } from '@/selectors/getCarDisclaimers';
import { TOOLTIP_TEXT } from '@/constants';
import Tooltip from '../Tooltip';
import Icon from '../Icon';

import style from './style.module.scss';

type TProps = {
  car: TBrandCar;
  disclaimers: TSortedDisclaimers;
};

const CarHeaderFixed: FC<TProps> = ({ car, disclaimers }) => {
  const elCarInfoRef = useRef<HTMLElement | null>(null);
  const elHeaderSite = document.querySelector('header.header') as HTMLElement;
  const [visibility, setVisibility] = useState(false);

  const images = getImages(car);

  const carImgUrl = images?.length ? images[0].url : null;
  const {
    summOfSelectedDiscountsWithoutTradeInPrice,
    carPriceDiscountedWithoutTradeInPrice,
    dealerCarPriceDiscountedWithoutTradeInPrice,
    dealerPrice,
    price
  } = car!.extra;
  const creditCalcFormState = useSelector(getCreditCalcFormState);
  const creditPrice = creditCalcFormState?.credits.monthPayment;

  const isDealerMode = WidgetMode.isDealerMode();

  const specifications = getSpecificationsForGroup(car!.specification, 'main', [
    'engine',
    'transmission',
    'drive',
  ]);
  const { grade, brand, model } = car!;
  const { engine, transmission, drive } = specifications;
  const specificationsDescr = `${engine} ${transmission} ${drive} ${grade}`;

  const TooltipPrice = (
    <Tooltip title={TOOLTIP_TEXT.price} placement="bottom">
      <Icon name="info" className="ml-2" />
    </Tooltip>
  );

  const toggleVisibilityHeader = (display: string) => {
    elHeaderSite.style.display = display;
  };

  const scrollHandler = useCallback(() => {
    const clientRect = elCarInfoRef.current?.getBoundingClientRect();

    if (!clientRect?.bottom) return;

    if (clientRect.bottom <= 0) {
      setVisibility(true);

      toggleVisibilityHeader('none');
    } else {
      setVisibility(false);
      toggleVisibilityHeader('block');
    }
  }, []);

  useEffect(() => {
    elCarInfoRef.current = document.getElementById('car-info-properties');
    window.addEventListener('scroll', scrollHandler);

    return () => {
      toggleVisibilityHeader('block');
      window.removeEventListener('scroll', scrollHandler);
    };
  }, []);

  return (
    <div
      className={cn(
        style.wrap,
        {
          [style.active]: visibility,
        },
        'pt-2 pt-md-0'
      )}
    >
      <div className="container">
        <div className="row flex-nowrap align-items-center">
          <div
            className={cn(
              style.carInfo,
              'col-auto flex-shrink-1 align-items-center mr-auto'
            )}
          >
            {carImgUrl && (
              <img className={style.carImg} src={carImgUrl} alt="poster" />
            )}
            <div className="ml-4 py-3">
              <div className={style.carModel}>{`${brand} ${model}`}</div>
              <div className={style.carDescr}>{specificationsDescr}</div>
            </div>
          </div>
          <div className="col-auto flex-shrink-0 py-2">
            <div className={style.priceTitle}>
              {summOfSelectedDiscountsWithoutTradeInPrice ? (
                <span>
                  Цена с учетом выгод
                  <sup>{disclaimers.get(Disclaimers.final)?.sortOrder}</sup>
                  {TooltipPrice}
                </span>
              ) : (
                <span>
                  Цена<sup>{disclaimers.get(Disclaimers.price)?.sortOrder}</sup>
                  {TooltipPrice}
                </span>
              )}
            </div>
            {summOfSelectedDiscountsWithoutTradeInPrice ? (
              <div
                className={cn(style.price, {
                  [style.priceWithDicsount]: summOfSelectedDiscountsWithoutTradeInPrice,
                })}
              >
                {format.price(isDealerMode ? dealerCarPriceDiscountedWithoutTradeInPrice : carPriceDiscountedWithoutTradeInPrice)} руб.
              </div>
            ) : (
              <div
                className={cn(style.price, {
                  [style.priceWithDicsount]: summOfSelectedDiscountsWithoutTradeInPrice,
                })}
              >
                {format.price(isDealerMode ? dealerPrice : price)} руб.
              </div>
            )}
          </div>
          {creditPrice && (
            <div className="col-auto flex-shrink-0 py-2 ml-auto ml-lg-5">
              <div className={style.priceTitle}>
                В кредит ежемесячно
                <Tooltip title={TOOLTIP_TEXT.monthly} placement="bottom">
                  <Icon name="info" className="ml-2" />
                </Tooltip>
              </div>
              <div className={style.price}>{creditPrice} руб.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CarHeaderFixed;
